import React, { useEffect, useState } from "react";
import { useMemberstack } from "@memberstack/react";
import { Link, useNavigate } from "react-router-dom";
import logoClotere from "./img/icn-logo.svg";

export default function Connexion() {
  const memberstack = useMemberstack();
  const navigate = useNavigate();

  const [formError, setFormError] = useState(false);
  const [formErrorMessage, setFormErrorMessage] = useState("");
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(false);

  // UI reset password
  // 0 = off, 1 = email -> envoi code, 2 = code + nouveau mdp
  const [resetStep, setResetStep] = useState(0);
  const [resetEmail, setResetEmail] = useState("");
  const [resetSent, setResetSent] = useState(false);
  const [resetToken, setResetToken] = useState("");
  const [resetNewPassword, setResetNewPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetDone, setResetDone] = useState(false);

  // Redirection si déjà connecté
  useEffect(() => {
    let mounted = true;
    memberstack.getCurrentMember().then(({ data }) => {
      if (!mounted) return;
      if (data) {
        setMember(data);
        navigate("/app/dashboard");
      }
    });
    return () => {
      mounted = false;
    };
  }, [memberstack, navigate]);

  // petit helper pour attendre que Memberstack recharge la session
  const waitForMember = async () => {
    let retries = 0;
    let m = null;

    while (!m && retries < 15) {
      const res = await memberstack.getCurrentMember();
      if (res?.data) {
        m = res.data;
        break;
      }
      await new Promise((resolve) => setTimeout(resolve, 300));
      retries++;
    }
    return m;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError(false);
    setFormErrorMessage("");
    setResetSent(false);
    setResetDone(false);
    setLoading(true);

    const email = event.target.email.value;
    const password = event.target.password.value;

    try {
      await memberstack.loginMemberEmailPassword({ email, password });

      const m = await waitForMember();
      if (!m) throw new Error("Impossible de charger le compte.");

      setMember(m);
      navigate("/app/dashboard");
    } catch (err) {
      console.error("Erreur login", err);
      setFormError(true);
      setFormErrorMessage(err?.message || "Erreur lors de la connexion");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkedInLogin = async () => {
    try {
      setLoading(true);
      setFormError(false);
      setFormErrorMessage("");
      setResetSent(false);
      setResetDone(false);
      setResetStep(0);

      // lance l'OAuth
      await memberstack.loginWithProvider({ provider: "linkedin" });

      // selon les setups, le callback OAuth ne déclenche pas toujours le useEffect
      // donc on force le navigate dès que la session est là
      const m = await waitForMember();
      if (m) {
        setMember(m);
        navigate("/app/dashboard");
      } else {
        throw new Error("Connexion LinkedIn OK, mais session non détectée.");
      }
    } catch (err) {
      console.error("Erreur login LinkedIn", err);
      setFormError(true);
      setFormErrorMessage(err?.message || "Erreur lors de la connexion LinkedIn");
    } finally {
      setLoading(false);
    }
  };

const handleSendResetCode = async () => {
  try {
    setFormError(false);
    setFormErrorMessage("");
    setResetSent(false);
    setResetDone(false); // ✅

    const email = resetEmail.trim();
    if (!email) {
      setFormError(true);
      setFormErrorMessage("Renseignez votre email pour recevoir le code.");
      return;
    }

    setLoading(true);
    await memberstack.sendMemberResetPasswordEmail({ email });

    setResetDone(false); // ✅ re-sécurité
    setResetSent(true);
    setResetStep(2);
  } catch (err) {
    console.error("Erreur reset password (send)", err);
    setFormError(true);
    setFormErrorMessage(err?.message || "Impossible d’envoyer le code.");
  } finally {
    setLoading(false);
  }
};

const handleConfirmReset = async () => {
  try {
    setFormError(false);
    setFormErrorMessage("");
    setResetDone(false);

    const token = resetToken.trim();
    if (!token) {
      setFormError(true);
      setFormErrorMessage("Renseignez le code reçu par email.");
      return;
    }

    if (!resetNewPassword || resetNewPassword.length < 8) {
      setFormError(true);
      setFormErrorMessage("Le mot de passe doit faire au moins 8 caractères.");
      return;
    }

    if (resetNewPassword !== resetConfirmPassword) {
      setFormError(true);
      setFormErrorMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    setLoading(true);

    await memberstack.resetMemberPassword({
      token,
      newPassword: resetNewPassword,
    });

    setResetSent(false); // ✅
    setResetDone(true);

    // revenir au login classique après succès
    setResetStep(0);
    setResetToken("");
    setResetNewPassword("");
    setResetConfirmPassword("");
  } catch (err) {
    console.error("Erreur reset password (confirm)", err);
    setFormError(true);
    setFormErrorMessage(err?.message || "Code invalide ou expiré.");
  } finally {
    setLoading(false);
  }
};

  if (member) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Link to="/" className="flex justify-center">
            <img src={logoClotere} alt="Clotere" className="h-12 w-auto" />
          </Link>

          <h2 className="mt-6 text-center text-2xl font-bold tracking-tight text-gray-900">
            Ravi de vous revoir
          </h2>

          <p className="mt-2 text-center text-sm text-gray-500">
            Pas encore de compte ?{" "}
            <Link
              to="/inscription"
              className="font-semibold text-indigo-600 hover:text-indigo-500"
            >
              Inscrivez-vous
            </Link>
          </p>
        </div>

        {/* Card */}
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-[480px]">
          {/* ✅ LinkedIn 
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={handleLinkedInLogin}
              disabled={loading}
              className={`flex w-full items-center justify-center gap-2 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 ${
                loading ? "opacity-60 cursor-not-allowed" : ""
              }`}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M20.447 20.452H17.21v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.083V9h3.112v1.561h.044c.434-.824 1.494-1.692 3.073-1.692 3.287 0 3.894 2.164 3.894 4.977v6.606ZM5.337 7.433a1.806 1.806 0 1 1 0-3.612 1.806 1.806 0 0 1 0 3.612ZM6.956 20.452H3.717V9h3.239v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.727v20.545C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.273V1.727C24 .774 23.2 0 22.222 0h.003Z"
                />
              </svg>
              Se connecter avec LinkedIn
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-gray-50 px-2 text-gray-500">ou</span>
              </div>
            </div>
          </div>
          */}

          <div className="bg-white px-6 py-10 shadow-sm sm:rounded-lg sm:px-12">
            {formError && (
              <div className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {formErrorMessage}
              </div>
            )}

            {resetSent && (
              <div className="mb-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                Code envoyé. Vérifiez votre boîte mail (et vos spams).
              </div>
            )}

            {resetDone && (
              <div className="mb-6 rounded-md border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                Mot de passe mis à jour. Vous pouvez vous connecter.
              </div>
            )}

            {/* ✅ RESET PASSWORD MODE : 1 = email / 2 = code+mdp */}
            {resetStep === 1 && (
              <div className="space-y-4">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  Renseignez votre email pour recevoir un <strong>code</strong> de réinitialisation.
                </div>

                <div>
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-900">
                    Email
                  </label>
                  <div className="mt-2">
                    <input
                      id="resetEmail"
                      name="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      autoComplete="email"
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="ex: prenom.nom@etude.fr"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setResetStep(0);
                      setFormError(false);
                      setFormErrorMessage("");
                      setResetSent(false);
                      setResetDone(false);
                      setResetToken("");
                      setResetNewPassword("");
                      setResetConfirmPassword("");
                    }}
                    className="text-sm font-semibold text-gray-600 hover:text-gray-900"
                    disabled={loading}
                  >
                    Retour
                  </button>

                  <button
                    type="button"
                    onClick={handleSendResetCode}
                    disabled={loading}
                    className={`rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? "Envoi…" : "Envoyer le code"}
                  </button>
                </div>
              </div>
            )}

            {resetStep === 2 && (
              <div className="space-y-4">
                <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                  Entrez le <strong>code</strong> reçu par email, puis choisissez un nouveau mot de passe.
                </div>

                <div>
                  <label htmlFor="resetToken" className="block text-sm font-medium text-gray-900">
                    Code (6 chiffres)
                  </label>
                  <div className="mt-2">
                    <input
                      id="resetToken"
                      name="resetToken"
                      type="text"
                      inputMode="numeric"
                      value={resetToken}
                      onChange={(e) => setResetToken(e.target.value)}
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="123456"
                    />
                  </div>
                  {resetEmail && (
                    <p className="mt-2 text-xs text-gray-500">
                      Code envoyé à <strong>{resetEmail}</strong>
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="resetNewPassword" className="block text-sm font-medium text-gray-900">
                    Nouveau mot de passe
                  </label>
                  <div className="mt-2">
                    <input
                      id="resetNewPassword"
                      name="resetNewPassword"
                      type="password"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="8 caractères minimum"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="resetConfirmPassword" className="block text-sm font-medium text-gray-900">
                    Confirmer le mot de passe
                  </label>
                  <div className="mt-2">
                    <input
                      id="resetConfirmPassword"
                      name="resetConfirmPassword"
                      type="password"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="Confirmez le mot de passe"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setResetStep(1);
                      setFormError(false);
                      setFormErrorMessage("");
                      setResetDone(false);
                    }}
                    className="text-sm font-semibold text-gray-600 hover:text-gray-900"
                    disabled={loading}
                  >
                    Retour
                  </button>

                  <button
                    type="button"
                    onClick={handleConfirmReset}
                    disabled={loading}
                    className={`rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? "Validation…" : "Réinitialiser"}
                  </button>
                </div>
              </div>
            )}

            {/* ✅ LOGIN MODE */}
            {resetStep === 0 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900">
                    Email
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      autoComplete="email"
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="ex: prenom.nom@etude.fr"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-900">
                    Mot de passe
                  </label>
                  <div className="mt-2">
                    <input
                      id="password"
                      name="password"
                      type="password"
                      required
                      minLength={8}
                      autoComplete="current-password"
                      className="block w-full rounded-md bg-white px-3 py-2 text-sm text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600"
                      placeholder="Votre mot de passe"
                    />
                  </div>

                  <div className="mt-2 flex items-center justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        const emailValue = document.getElementById("email")?.value?.trim() || "";
                        setResetEmail(emailValue);
                        setResetSent(false);
                        setResetDone(false);
                        setResetToken("");
                        setResetNewPassword("");
                        setResetConfirmPassword("");
                        setFormError(false);
                        setFormErrorMessage("");
                        setResetStep(1);
                      }}
                      className="text-sm font-semibold text-indigo-600 hover:text-indigo-500"
                      disabled={loading}
                    >
                      Mot de passe oublié ?
                    </button>
                  </div>
                </div>

                <div>
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 ${
                      loading ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {loading ? "Connexion…" : "Se connecter"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}